import type {
  CategoryColor,
  Order,
  OrderItemStatus,
  OrderStatus,
  PaymentMethod,
  PreparationStatus,
  PrintDriver,
  ServiceType,
  TableStatus,
} from '@horevia/contracts';
import type { OrderStatusTone } from '@horevia/ui';
import {
  Beef,
  Beer,
  Cake,
  Coffee,
  CookingPot,
  Croissant,
  Fish,
  GlassWater,
  IceCream,
  Pizza,
  Salad,
  Sandwich,
  Soup,
  Utensils,
  Wine,
  type LucideIcon,
} from 'lucide-react';

/** Icônes Lucide proposées pour les catégories (nom kebab-case ↔ composant). */
export const CATEGORY_ICONS: Record<string, LucideIcon> = {
  utensils: Utensils,
  'cooking-pot': CookingPot,
  soup: Soup,
  salad: Salad,
  beef: Beef,
  fish: Fish,
  pizza: Pizza,
  sandwich: Sandwich,
  croissant: Croissant,
  cake: Cake,
  'ice-cream': IceCream,
  coffee: Coffee,
  'glass-water': GlassWater,
  beer: Beer,
  wine: Wine,
};
export const CATEGORY_ICON_NAMES = Object.keys(CATEGORY_ICONS);
export const CATEGORY_COLORS: CategoryColor[] = ['GREEN', 'GOLD', 'SLATE', 'OLIVE', 'TERRACOTTA'];

export function categoryIcon(name: string): LucideIcon {
  return CATEGORY_ICONS[name] ?? Utensils;
}

export const SERVICE_TYPES: ServiceType[] = [
  'DINE_IN',
  'COUNTER',
  'TAKEAWAY',
  'DELIVERY',
  'ROOM_SERVICE',
];
export const ORDER_STATUSES: OrderStatus[] = ['OPEN', 'PAID', 'CANCELLED', 'TRANSFERRED'];
export const PREPARATION_STATUSES: PreparationStatus[] = [
  'NEW',
  'IN_PREPARATION',
  'READY',
  'SERVED',
];
export const PRINT_DRIVERS: PrintDriver[] = ['BROWSER', 'ESCPOS_NETWORK', 'NONE'];

/** Moyens de paiement qu'un point de vente peut accepter à la caisse (ADR-026, D-H78). */
export const POS_PAYMENT_METHOD_CHOICES: PaymentMethod[] = [
  'CASH',
  'MOBILE_MONEY',
  'CARD',
  'BANK_TRANSFER',
  'CHEQUE',
  'OTHER',
];

/** État dérivé d'une table depuis sa commande ouverte (jamais stocké). */
export function tableStatusOf(order: Order | undefined): TableStatus {
  if (!order) return 'FREE';
  return order.billRequestedAt ? 'BILL_REQUESTED' : 'OCCUPIED';
}

const ORDER_TONES: Record<OrderStatus, OrderStatusTone> = {
  OPEN: 'new',
  PAID: 'paid',
  TRANSFERRED: 'transferred',
  CANCELLED: 'cancelled',
};
const PREPARATION_TONES: Record<PreparationStatus, OrderStatusTone> = {
  NEW: 'new',
  IN_PREPARATION: 'sent',
  READY: 'ready',
  SERVED: 'served',
};
const ITEM_TONES: Record<OrderItemStatus, OrderStatusTone> = {
  PENDING: 'new',
  SENT: 'sent',
  READY: 'ready',
  SERVED: 'served',
  CANCELLED: 'cancelled',
};

export function orderTone(status: OrderStatus): OrderStatusTone {
  return ORDER_TONES[status];
}
export function preparationTone(status: PreparationStatus): OrderStatusTone {
  return PREPARATION_TONES[status];
}
export function itemTone(status: OrderItemStatus): OrderStatusTone {
  return ITEM_TONES[status];
}

/**
 * Statut affiché sur une carte de commande ouverte : l'état de préparation
 * tant que la commande n'est pas close, sinon son statut.
 */
export function displayTone(status: OrderStatus, preparation: PreparationStatus): OrderStatusTone {
  return status === 'OPEN' ? preparationTone(preparation) : orderTone(status);
}

/** Une commande se modifie tant qu'elle est ouverte (R-POS-05). */
export function isOrderEditable(status: OrderStatus): boolean {
  return status === 'OPEN';
}

/** Ancienneté d'un ticket en minutes, pour l'écran cuisine (seuil de retard à 15 min). */
export function ticketAgeMinutes(createdAt: string, now = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(createdAt).getTime()) / 60_000));
}
export const KITCHEN_LATE_MINUTES = 15;
